import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep
} from 'components/final-form/dropdownUtils';
import { FormState } from 'final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import {
  ArticleGroupDto,
  CompanyDto,
  CompanySearchRequest,
  DivisionDto,
  EmployeeDto,
  MedicamentDto,
  StockDataDto,
  StockDataSearchRequest,
  TestLaboratoryDto
} from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { Dimmer, Grid, Loader, Message } from 'semantic-ui-react';
import { getAllArticleGroups } from 'services/articleGroupServices';
import { getAllShippableMedicaments } from 'services/medicamentServices';
import { searchCompanies } from 'services/companyServices';
import { debounce } from 'ts-debounce';
import { noop } from 'utils/functionUtils';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import PxGrid from 'components/layout/PxGrid';
import Select from 'components/final-form/Select';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { searchStockData } from 'services/stockDataServices';
import { getAllTestLaboratory } from 'services/testLaboratoryServices';
import styled from 'styled-components';
import DatePicker from 'components/final-form/DatePicker';
import moment from 'moment';
import SearchResultComponent from 'components/SearchResultComponent';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';

const StyledCell = styled.div`
  text-align: right;
  padding-right: 5rem;
`;

interface StockDataFieldProps extends FormState {
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  referenceDataLoaded: boolean,
  searchValues: Partial<StockDataSearchRequest>,
  foundStockData: Array<StockDataDto>,
  stockDataLoaded: boolean,
  articleGroups: Array<ArticleGroupDto>,
  articleGroupOptions: Array<DropdownOption>,
  companies: Array<CompanyDto>,
  companyOptions: Array<DropdownOption>,
  searchCompaniesValues: Partial<CompanySearchRequest>,
  shippableMaterials: Array<MedicamentDto>,
  shippableMaterialOptions: Array<DropdownOption>,
  testLaboratories: Array<TestLaboratoryDto>,
  employees: Array<EmployeeDto>,
  divisions: Array<DivisionDto>,
  loading: boolean;
  sumOfQuantities: number;
  cancelTokenSource: CancelTokenSource;
  errorMessage?: String;
}

class StockDataView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialSearchValues: Partial<StockDataSearchRequest> = {
    medicamentId: undefined,
    articleGroupId: undefined,
    companyId: undefined,
    fromDate: undefined,
    toDate: undefined
  }

  constructor(props: Props) {
    super(props);

    const {getMessage} = props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      referenceDataLoaded: false,

      searchValues: {
        medicamentId: undefined,
        articleGroupId: undefined,
        companyId: undefined
      },

      foundStockData: [],
      stockDataLoaded: true,
      articleGroups: [],
      articleGroupOptions: [],
      shippableMaterials: [],
      shippableMaterialOptions: [],
      companies: [],
      companyOptions: [],
      searchCompaniesValues: {},
      testLaboratories: [],
      employees: [],
      divisions: [],
      sumOfQuantities: 0,

      loading: false,
      cancelTokenSource
    };

    this.loadReferenceData(cancelTokenSource);

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {
    try {
      const {currentUser} = this.props;
      const {searchCompaniesValues} = this.state;
      searchCompaniesValues.mandateId = currentUser!.userConfig.mandateId;

      const articleGroups = await getAllArticleGroups(cancelTokenSource);
      const shippableMaterials = await getAllShippableMedicaments(cancelTokenSource);
      const companiesResponse = await searchCompanies(searchCompaniesValues, cancelTokenSource);
      const companies = companiesResponse.filter(c => c.guarantorCompany === false);
      const testLaboratories = await getAllTestLaboratory(cancelTokenSource);

      this.setState({
        articleGroups: articleGroups ? articleGroups : [],
        articleGroupOptions: mapToDropdownOptionArrayWithSep(
            articleGroups, ["description", "externalOrderNumber", "testLaboratoryDescription"], "id"),
        shippableMaterials: shippableMaterials ? shippableMaterials : [],
        shippableMaterialOptions: mapToDropdownOptionArray(
            shippableMaterials, ["code", "description"], "id", "id"),
        companies: companies ? companies : [],
        companyOptions: mapToDropdownOptionArray(
            companies, ["description", "city"], "id", "id"),
        testLaboratories: testLaboratories ? testLaboratories : []
      });

    } finally {
      this.setState({
        referenceDataLoaded: true
      });
    }
  }

  handleChange = debounce(({values}: StockDataFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    this.setState({
      searchValues: values
    });

    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        stockDataLoaded: true
      })
    };

    this.setState({stockDataLoaded: false});

    searchStockData(values, cancelTokenSource)
        .then(response => {
              let sumOfQuantities = 0;
              if (response.length > 0) {
                response.forEach(s => sumOfQuantities += s.quantity);
              }
              this.setState({
                foundStockData: response,
                sumOfQuantities: sumOfQuantities
              })
            }
        )
        .catch(noop)
        .finally(onFinally);
  }, 300)

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: string[] = [];

    if (knownErrors.includes(errorCode)) {
      this.setErrorMessage(getMessage(`stockOrder.error.${errorCode}`))
    } else {
      this.setErrorMessage(getMessage('error.general'))
    }
  }

  setErrorMessage = (error?: String) => {
    this.setState({
      errorMessage: error
    })
  }

  render(): React.ReactNode {
    const {stockDataLoaded} = this.state;
    return (
        <PraxStyledSegment>
          <div>
            {this.renderSearchStockDataSegment()}
          </div>

          <StyledViewTableRow>
            {!stockDataLoaded
                ? this.renderLoader()
                : this.renderStockDataSegment()}
          </StyledViewTableRow>
        </PraxStyledSegment>
    );
  }

  renderSearchStockDataSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialSearchValues}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;
    const {
      errorMessage,
      companyOptions,
      articleGroupOptions,
      shippableMaterialOptions,
      sumOfQuantities,
      foundStockData
    } = this.state;
    const disabledBefore = values.fromDate && new Date(values.fromDate);

    const numResults = foundStockData.length;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("company.title")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="companyId"
                  component={Select}
                  options={companyOptions}
                  clearable
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={2}>
              {getMessage("from")}:
            </Grid.Column>
            <Grid.Column width={3}>
              <Field
                  name="fromDate"
                  component={DatePicker}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("stockOrder.articleGroupId")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="articleGroupId"
                  component={Select}
                  options={articleGroupOptions}
                  clearable
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={2}>
              {getMessage("to")}:
            </Grid.Column>
            <Grid.Column width={3}>
              <Field
                  name="toDate"
                  component={DatePicker}
                  disabledBefore={disabledBefore}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("stockOrders.search.shippable")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="medicamentId"
                  component={Select}
                  options={shippableMaterialOptions}
                  clearable
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={2} verticalAlign={'middle'}>
              {getMessage("stockData.sumOfQuantities")}:
            </Grid.Column>
            <Grid.Column width={3} verticalAlign={'middle'}>
              <strong>{sumOfQuantities}</strong>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}}
                   onChange={formState => this.handleChange(formState)}
          />
        </PxGrid>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("stockData.loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderStockDataSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundStockData} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundStockData.length}
            rowGetter={this.stockDataRowGetter}
            rowRenderer={this.foundStockDataRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 50,
                label: "#",
                dataKey: "index"
              },
              {
                width: 350,
                label: getMessage("stockOrder.companyId"),
                dataKey: "companyId",
                cellRenderer: this.companyCellRenderer
              },
              {
                width: 350,
                label: getMessage("stockOrder.divisionId"),
                dataKey: "divisionName"
              },
              {
                width: 350,
                label: getMessage("stockOrder.employeeId"),
                dataKey: "employeeId",
                cellRenderer: this.employeeCelRenderer
              },
              {
                width: 130,
                label: getMessage("stockOrder.quantity"),
                dataKey: "quantity",
                cellRenderer: this.quantityCellRenderer
              },
              {
                width: 450,
                label: getMessage("stockOrder.articleGroupId"),
                dataKey: "articleGroupId",
                cellRenderer: this.articleGroupCellRenderer
              },
              // {
              //   width: 350,
              //   label: getMessage("stockOrder.medicamentId"),
              //   dataKey: "medicamentId",
              //   cellRenderer: this.shippableCellRenderer
              // },
              {
                width: 150,
                label: getMessage("stockData.creator"),
                dataKey: "creator",
                cellRenderer: this.stockDataCreatorCellRenderer
              },
              {
                width: 150,
                label: getMessage("stockOrder.deliverDate"),
                dataKey: "deliverDate",
                cellRenderer: this.dateCellRenderer
              }
            ]}
        />
    );
  };

  stockDataRowGetter = ({index}: any) => {
    const {foundStockData} = this.state;

    Object.assign(foundStockData[index], {index: index + 1});

    return foundStockData[index];
  };

  foundStockDataRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  quantityCellRenderer = ({cellData, value}: any) => {
    const {articleGroups} = this.state;

    if (cellData) {
      // let packageQuantity = 0;
      // if (value.articleGroupId) {
      //   const articleGroup = articleGroups.find(a => a.id === value.articleGroupId);
      //   if (articleGroup) {
      //     packageQuantity = articleGroup.packageQuantity;
      //     cellData = cellData * packageQuantity;
      //   }
      // }
      return (
          <StyledCell>{(cellData)}</StyledCell>
      );
    }

    return (
        <div></div>
    );
  }

  employeeCelRenderer = ({rowData}: any) => {

    if (rowData.employeeId > 0) {
      const desc = rowData.employeeFirstName + ", " + rowData.employeeName;
      return desc;
    } else {
      return "";
    }
  }

  articleGroupCellRenderer = ({cellData}: any) => {
    const {articleGroups} = this.state;

    if (cellData && cellData > 0) {
      const foundAG = articleGroups.filter((ag: ArticleGroupDto) => ag.id === cellData);
      if (foundAG && foundAG.length > 0) {
        let desc = foundAG[0].description;
        if (foundAG[0].externalOrderNumber) {
          desc = desc + ", " + foundAG[0].externalOrderNumber;
        }
        if (foundAG[0].testLaboratoryDescription) {
          desc = desc + ", " + foundAG[0].testLaboratoryDescription;
        }
        return desc;
      }
    }
    return "";
  }


  shippableCellRenderer = ({cellData}: any) => {
    const {shippableMaterials} = this.state;

    if (cellData && cellData > 0) {
      const foundShippable = shippableMaterials.filter((m: MedicamentDto) => m.id === cellData)
      if (foundShippable && foundShippable.length > 0) {
        let desc = foundShippable[0].description;
        if (foundShippable[0].externalOrderNumber) {
          desc = desc + ", " + foundShippable[0].externalOrderNumber
        }
        return desc;
      }
    }
    return "";
  }

  companyCellRenderer = ({cellData}: any) => {
    const {companies} = this.state;

    if (cellData && cellData > 0) {
      const foundCompany = companies.filter((company: CompanyDto) => company.id === cellData)
      if (foundCompany && foundCompany.length > 0) {
        return foundCompany[0].description;
      }
    }
    return "";
  }

  stockDataCreatorCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage(`enum.creator.${cellData}`)
    }
  }

  dateCellRenderer = ({cellData}: any) => {

    if (cellData) {
      return moment(cellData).format("DD.MM.YYYY")
    }
    return "";
  }
}

export default withMessages(withAuthContext(StockDataView));

